import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Shield,
  Users,
  Package,
  Factory,
  ClipboardCheck,
  Boxes,
  Wrench,
  Settings,
  BarChart3,
  GitBranch,
  CheckCircle2,
} from "lucide-react";
import { cn } from "@/lib/utils";

const roles = [
  { id: "admin", name: "Administrador", color: "bg-red-500/10 text-red-500", description: "Acceso total" },
  { id: "supervisor", name: "Supervisor", color: "bg-purple-500/10 text-purple-500", description: "Gestion de produccion" },
  { id: "operator", name: "Operador", color: "bg-blue-500/10 text-blue-500", description: "Operaciones basicas" },
  { id: "quality", name: "Calidad", color: "bg-green-500/10 text-green-500", description: "Control de calidad" },
  { id: "warehouse", name: "Almacen", color: "bg-orange-500/10 text-orange-500", description: "Gestion de inventario" },
  { id: "maintenance", name: "Mantenimiento", color: "bg-cyan-500/10 text-cyan-500", description: "Maquinas y moldes" },
  { id: "viewer", name: "Visor", color: "bg-gray-500/10 text-gray-500", description: "Solo lectura" },
];

const moduleIcons: Record<string, any> = {
  work_orders: Package,
  production: Factory,
  quality: ClipboardCheck,
  inventory: Boxes,
  molds: Wrench,
  machines: Settings,
  users: Users,
  reports: BarChart3,
  settings: Settings,
  traceability: GitBranch,
};

export function RolesPermissionsView() {
  const { data: permissions } = useQuery({
    queryKey: ["permissions"],
    queryFn: async () => {
      const { data, error } = await api
        .from("permissions")
        .select("*")
        .order("module")
        .order("code");
      if (error) throw error;
      return data;
    },
  });

  const { data: rolePermissions } = useQuery({
    queryKey: ["role-permissions"],
    queryFn: async () => {
      const { data, error } = await api.from("role_permissions").select("*");
      if (error) throw error;
      return data;
    },
  });

  // Group permissions by module
  const permissionsByModule = permissions?.reduce((acc: Record<string, any[]>, perm) => {
    const module = perm.module || "other";
    if (!acc[module]) acc[module] = [];
    acc[module].push(perm);
    return acc;
  }, {}) || {};

  // Check if role has permission
  const hasPermission = (roleId: string, permissionId: string) => {
    return rolePermissions?.some((rp) => rp.role === roleId && rp.permission_id === permissionId);
  };

  const getModuleLabel = (module: string) => {
    const labels: Record<string, string> = {
      work_orders: "Ordenes de Trabajo",
      production: "Produccion",
      quality: "Calidad",
      inventory: "Inventario",
      molds: "Moldes",
      machines: "Maquinas",
      users: "Usuarios",
      reports: "Reportes",
      settings: "Configuracion",
      traceability: "Trazabilidad",
    };
    return labels[module] || module;
  };

  return (
    <div className="space-y-6">
      {/* Roles Overview */}
      <div className="grid gap-4 md:grid-cols-4 lg:grid-cols-7">
        {roles.map((role) => (
          <Card key={role.id}>
            <CardContent className="pt-4">
              <div className="text-center">
                <Badge className={cn("mb-2", role.color)}>{role.name}</Badge>
                <p className="text-xs text-muted-foreground">{role.description}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Permissions Matrix */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Matriz de Permisos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion type="multiple" className="w-full">
            {Object.entries(permissionsByModule).map(([module, modulePermissions]) => {
              const Icon = moduleIcons[module] || Package;
              return (
                <AccordionItem key={module} value={module}>
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4" />
                      <span>{getModuleLabel(module)}</span>
                      <Badge variant="outline" className="ml-2">
                        {modulePermissions.length} permisos
                      </Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-2 w-64">Permiso</th>
                            {roles.map((role) => (
                              <th key={role.id} className="text-center p-2 w-24">
                                <Badge className={cn("text-xs", role.color)}>{role.name}</Badge>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {modulePermissions.map((perm: any) => (
                            <tr key={perm.id} className="border-b hover:bg-muted/30">
                              <td className="p-2">
                                <div>
                                  <p className="font-medium">{perm.name}</p>
                                  <p className="text-xs text-muted-foreground">{perm.code}</p>
                                </div>
                              </td>
                              {roles.map((role) => (
                                <td key={role.id} className="text-center p-2">
                                  {hasPermission(role.id, perm.id) ? (
                                    <CheckCircle2 className="w-5 h-5 text-green-500 mx-auto" />
                                  ) : (
                                    <div className="w-5 h-5 rounded-full border-2 border-muted mx-auto" />
                                  )}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </CardContent>
      </Card>

      {/* Role Descriptions */}
      <Card>
        <CardHeader>
          <CardTitle>Descripcion de Roles</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            {roles.map((role) => (
              <div key={role.id} className="p-4 rounded-lg border">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={role.color}>{role.name}</Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  {role.id === "admin" &&
                    "Acceso completo a todas las funciones del sistema. Puede crear y eliminar usuarios, modificar configuracion global y acceder a todos los modulos."}
                  {role.id === "supervisor" &&
                    "Puede gestionar la produccion, aprobar inspecciones de calidad, ver reportes y gestionar el inventario. No puede eliminar usuarios ni modificar configuracion critica."}
                  {role.id === "operator" &&
                    "Acceso limitado a las funciones operativas. Puede ver ordenes de trabajo, iniciar procesos de produccion y registrar su actividad."}
                  {role.id === "quality" &&
                    "Especializado en control de calidad. Puede crear, editar y aprobar inspecciones de calidad, registrar defectos y ver reportes de calidad."}
                  {role.id === "warehouse" &&
                    "Especializado en gestion de almacen. Puede gestionar el inventario, registrar entradas y salidas de material y gestionar la trazabilidad."}
                  {role.id === "maintenance" &&
                    "Especializado en mantenimiento. Puede gestionar maquinas, moldes, registrar mantenimientos y ver el estado de los equipos."}
                  {role.id === "viewer" &&
                    "Acceso de solo lectura. Puede visualizar la informacion de todos los modulos pero no puede realizar ninguna modificacion."}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
